import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isProcessingStatusInProgress, processingStatusLabel } from '../../../../utils/processingStatus';

export type ProgressInfo = { stage?: string; percent?: number; ts?: number } | undefined;

type Props = {
  status?: string | null;
  progress?: ProgressInfo;
  className?: string;
  small?: boolean;
  tick?: number;
};

const ProcessingStatusPill: React.FC<Props> = ({ status, progress, className = '', small = true, tick }) => {
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    const meta = progress as any;
    if (isProcessingStatusInProgress(status) || meta?.stage === 'reclassifying') {
      const base = meta?.startedAt ?? progress?.ts;
      if (typeof base === 'number') {
        if (startRef.current === null || base < startRef.current) {
          startRef.current = base;
        }
      }
    } else {
      startRef.current = null;
    }
  }, [status, (progress as any)?.stage, progress?.ts]);

  const { visible, label, pct, updated, isTerminalStage } = useMemo(() => {
    if (!status && !progress) return { visible: false, label: '', pct: 0, updated: undefined as string | undefined, isTerminalStage: false };
    // Every state with work still owed shows, not just 'pending' — a signal
    // waiting on the redaction sweep is in progress, and hiding it makes the
    // record look finished.
    if (!isProcessingStatusInProgress(status) && !progress) return { visible: false, label: '', pct: 0, updated: undefined, isTerminalStage: false };
    const meta = progress as any;
    const stage = progress?.stage || meta?.stage || 'processing';
    const isCompleted =
      stage === 'done' || status === 'classified' || status === 'done' ||
      // `failed` is finished; treating it as in-flight is what left the feed
      // pulsing after a failure. `redaction_blocked` is NOT finished — it is
      // waiting on the sweep — so it keeps pulsing like any other queued row.
      stage === 'failed';
    // No live progress: the stored status is all we have, so name it.
    const label = !progress
      ? processingStatusLabel(status)
      : stage === 'failed'
      // The pill had no failed branch and fell through to a pulsing
      // "Processing", so a run the API reports as failed stayed visually in
      // flight. Terminal states take their wording from the shared mapping.
      ? processingStatusLabel(stage)
      : stage === 'redaction_blocked'
      // A blocked signal arrives as a progress event so it reaches open clients,
      // but it is a stored status, not a live stage — take its wording from the
      // one mapping rather than letting it fall through to 'Processing'.
      ? processingStatusLabel(stage)
      : isCompleted
      ? 'Completed'
      : stage === 'generating_embeddings'
      ? 'Generating embeddings'
      : stage === 'reclassifying'
      ? 'Reclassifying'
      : stage === 'summarising'
      ? 'Summarising'
      : stage === 'evaluating_ombudsman'
      ? 'Evaluating ombudsman'
      : 'Processing';
    const pct = Math.max(5, Math.min(100, progress?.percent ?? (meta?.percent ?? 15)));
    const now = Date.now();
    const referenceTs = meta?.lastRealtimeAt ?? startRef.current ?? meta?.startedAt ?? progress?.ts;
    const updated = referenceTs
      ? (() => {
          const diff = Math.max(0, now - referenceTs);
          const s = Math.floor(diff / 1000);
          if (s < 5) return 'just now';
          if (s < 60) return `${s}sec`;
          const m = Math.floor(s / 60);
          if (m < 60) return `${m}min`;
          const h = Math.floor(m / 60);
          if (h < 24) return `${h}h`;
          const d = Math.floor(h / 24);
          return `${d}d`;
        })()
      : undefined;
    return {
      visible: true,
      label,
      pct,
      updated,
      // Only `failed` is terminal. `redaction_blocked` reads as "Queued" and is
      // waiting on the recovery sweep, so showing it as a finished-and-stopped
      // state contradicts its own label.
      isTerminalStage: stage === 'failed',
    };
  }, [status, progress, tick]);

  // Smooth fade/hide timers for Completed state (independent of tick)
  const [fadeOut, setFadeOut] = useState(false);
  const [hidden, setHidden] = useState(false);
  const fadeTimerRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);

  useEffect(() => {
    // Reset on any non-completed state
    const isCompleted = visible && progress && (progress.stage === 'done' || status === 'classified' || status === 'done');
    // Clear existing timers
    if (fadeTimerRef.current) { window.clearTimeout(fadeTimerRef.current); fadeTimerRef.current = null; }
    if (hideTimerRef.current) { window.clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
    setFadeOut(false);
    setHidden(false);

    if (isCompleted) {
      const baseTs = progress?.ts ?? Date.now();
      const fadeAt = Math.max(0, baseTs + 9500 - Date.now());
      const hideAt = Math.max(0, baseTs + 10000 - Date.now());
      fadeTimerRef.current = window.setTimeout(() => setFadeOut(true), fadeAt);
      hideTimerRef.current = window.setTimeout(() => setHidden(true), hideAt);
    }

    return () => {
      if (fadeTimerRef.current) { window.clearTimeout(fadeTimerRef.current); fadeTimerRef.current = null; }
      if (hideTimerRef.current) { window.clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
    };
  }, [visible, status, progress?.stage, progress?.ts]);

  if (!visible || hidden) return null;

  const sizeClasses = small ? 'text-[10px] px-2 py-0.5' : 'text-xs px-2.5 py-1';

  return (
    <span className={`inline-flex items-center gap-1 font-semibold text-[#4342FF] bg-[#EEF2FF] border border-[#DDE2FF] rounded-full ${sizeClasses} ${className} ${fadeOut ? 'opacity-0 transition-opacity duration-700' : 'opacity-100 transition-opacity duration-300'}`}>
      {/* A terminal state never pulses: the dot is the "work is happening"
          signal, and animating it after a failure is what kept the feed looking
          in flight while the API reported failed. */}
      <span className={`inline-block w-1.5 h-1.5 ${isTerminalStage ? 'bg-red-500' : pct! < 100 ? 'bg-[#4342FF] animate-pulse' : 'bg-green-600'} rounded-full`} />
      <span>{label}</span>
      {updated && <span className="text-[#7A7A7A]"> {updated}</span>}
    </span>
  );
};

export default ProcessingStatusPill;
